/**
 * Environment configuration utility
 * Centralizes all environment variables with type safety and validation
 */

interface EnvironmentConfig {
    // API Configuration
    API_BASE_URL: string;
    API_KEY?: string;
    APP_ENV: 'development' | 'staging' | 'production';

    // App Configuration
    APP_TITLE: string;
    APP_DESCRIPTION: string;
    APP_URL: string;
    WEB_BASE_URL: string;

    // External Services
    GA_TRACKING_ID?: string;
    HOTJAR_ID?: string;
    FACEBOOK_PIXEL_ID?: string;

    // App Store Links
    IOS_APP_STORE_URL: string;
    ANDROID_PLAY_STORE_URL: string;

    // Feature Flags
    ENABLE_ANALYTICS: boolean;
    ENABLE_CONTACT_FORM: boolean;
    ENABLE_REFERRAL_SYSTEM: boolean;
}

const getEnvVar = (key: string, defaultValue?: string): string => {
    const value = import.meta.env[key] || defaultValue;
    if (!value && !defaultValue) {
        console.warn(`Environment variable ${key} is not defined - config.ts:35`);
    }
    return value || '';
};

const getBooleanEnvVar = (key: string, defaultValue: boolean = false): boolean => {
    const value = import.meta.env[key];
    if (value === undefined) return defaultValue;
    return value === 'true' || value === '1';
};

export const config: EnvironmentConfig = {
    // API Configuration
    API_BASE_URL: getEnvVar('VITE_API_BASE_URL', 'https://api.swagshots.com'),
    API_KEY: getEnvVar('VITE_API_KEY'),
    APP_ENV: (getEnvVar('VITE_APP_ENV', 'production') as EnvironmentConfig['APP_ENV']),

    // App Configuration
    APP_TITLE: getEnvVar('VITE_APP_TITLE', 'Swag Shots'),
    APP_DESCRIPTION: getEnvVar('VITE_APP_DESCRIPTION', 'Create. Connect. Earn.'),
    APP_URL: getEnvVar('VITE_APP_URL', 'https://www.swagshots.com'),
    WEB_BASE_URL: getEnvVar('VITE_WEB_BASE_URL', 'https://swagshots.com').replace(/\/+$/, ''),

    // External Services
    GA_TRACKING_ID: getEnvVar('VITE_GA_TRACKING_ID'),
    HOTJAR_ID: getEnvVar('VITE_HOTJAR_ID'),
    FACEBOOK_PIXEL_ID: getEnvVar('VITE_FACEBOOK_PIXEL_ID'),

    // App Store Links
    IOS_APP_STORE_URL: getEnvVar('VITE_IOS_APP_STORE_URL', ''),
    ANDROID_PLAY_STORE_URL: getEnvVar('VITE_ANDROID_PLAY_STORE_URL', 'https://play.google.com/store/apps/details?id=com.swagshots.app'),

    // Feature Flags
    ENABLE_ANALYTICS: getBooleanEnvVar('VITE_ENABLE_ANALYTICS', false),
    ENABLE_CONTACT_FORM: getBooleanEnvVar('VITE_ENABLE_CONTACT_FORM', true),
    ENABLE_REFERRAL_SYSTEM: getBooleanEnvVar('VITE_ENABLE_REFERRAL_SYSTEM', true),
};

// Development helpers
export const isDevelopment = config.APP_ENV === 'development';
export const isProduction = config.APP_ENV === 'production';
export const isStaging = config.APP_ENV === 'staging';

// Validation
if (isProduction && !config.API_BASE_URL) {
    throw new Error('API_BASE_URL is required in production environment');
}

console.log('🚀 Environment: - config.ts:82', config.APP_ENV);
if (isDevelopment) {
    console.log('📊 Config: - config.ts:84', config);
}
